import type { SessionView, SystemCapabilities, CompanyPage, CompanyToolInventory, SyncHistoryPage } from './backend';
import { ApiError } from './http';

/**
 * Runtime validation of implemented-now responses against the pinned contract
 * (docs/openapi.yaml, sha256 45d52b69…). Generated compile-time types are not
 * validation of received JSON (handoff §2); these guards are. A violation is a
 * client-visible integrity failure, never silently coerced.
 */

function fail(route: string, detail: string): never {
  throw new ApiError(
    'CONTRACT_VIOLATION',
    `The ${route} response does not match the pinned contract: ${detail}`,
    false,
    null,
    200,
  );
}

const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

const AUTH_METHODS = ['GCP_IAP', 'DEVELOPMENT_BYPASS'] as const;
const ROLES = ['VIEWER', 'REVIEWER', 'OPERATOR', 'ADMIN'] as const;

export function validateSession(v: unknown): SessionView {
  const s = v as Record<string, unknown>;
  if (typeof s !== 'object' || s === null) fail('session', 'body is not an object');
  if (s.authenticated !== true) fail('session', 'authenticated is not the constant true');
  if (!AUTH_METHODS.includes(s.authenticationMethod as never)) fail('session', `authenticationMethod ${String(s.authenticationMethod)}`);
  if (!ROLES.includes(s.role as never)) fail('session', `role ${String(s.role)} is outside the wire enum`);
  if (!isStr(s.region)) fail('session', 'region missing');
  for (const opt of ['hostedDomain', 'email', 'pictureUrl', 'expiresAt'] as const) {
    if (s[opt] !== undefined && !isStr(s[opt])) fail('session', `${opt} present but not a string`);
  }
  return s as unknown as SessionView;
}

const PRODUCT_KEYS = [
  'sourceCatalog', 'companyCatalog', 'toolInventory', 'attribution', 'analysis',
  'reconstruction', 'preview', 'reporting', 'recency', 'liveness',
  'commercialReadiness', 'graph', 'walkthrough', 'search', 'chat',
  'packaging', 'sync', 'delivery', 'operations',
] as const;
const PRODUCT_STATES = ['IMPLEMENTED', 'UNAVAILABLE'] as const;
const COMPONENT_STATES = ['COMPILED', 'CONFIGURED', 'LIVE_VERIFIED', 'UNAVAILABLE'] as const;
const RUNTIMES = ['DEVELOPMENT', 'TEST', 'PRODUCTION'] as const;

export function validateCapabilities(v: unknown): SystemCapabilities {
  const c = v as Record<string, unknown>;
  if (typeof c !== 'object' || c === null) fail('capabilities', 'body is not an object');
  if (c.apiVersion !== 'v1') fail('capabilities', `apiVersion ${String(c.apiVersion)}`);
  if (!RUNTIMES.includes(c.runtimeEnvironment as never)) fail('capabilities', `runtimeEnvironment ${String(c.runtimeEnvironment)}`);

  const caps = c.capabilities as Record<string, unknown>;
  if (typeof caps !== 'object' || caps === null) fail('capabilities', 'capabilities record missing');
  for (const k of PRODUCT_KEYS) {
    if (!PRODUCT_STATES.includes(caps[k] as never)) fail('capabilities', `capabilities.${k} is ${String(caps[k])}`);
  }

  const ret = c.derivedRetention as Record<string, unknown>;
  if (typeof ret !== 'object' || ret === null) fail('capabilities', 'derivedRetention missing');
  if (!PRODUCT_STATES.includes(ret.state as never)) fail('capabilities', 'derivedRetention.state invalid');
  if (!isStr(ret.basis)) fail('capabilities', 'derivedRetention.basis missing');
  if (!Array.isArray(ret.policies)) fail('capabilities', 'derivedRetention.policies is not an array');
  for (const p of ret.policies as Record<string, unknown>[]) {
    if (!isStr(p.artifactClass) || typeof p.retentionSeconds !== 'number') fail('capabilities', 'retention policy row malformed');
  }

  if (!Array.isArray(c.components) || c.components.length < 1) fail('capabilities', 'components missing');
  for (const comp of c.components as Record<string, unknown>[]) {
    if (!isStr(comp.capability) || !isStr(comp.basis) || !COMPONENT_STATES.includes(comp.state as never))
      fail('capabilities', 'component row malformed');
  }
  if (!Array.isArray(c.tools)) fail('capabilities', 'tools missing');
  if (!Array.isArray(c.knownGaps) || !(c.knownGaps as unknown[]).every((g) => typeof g === 'string'))
    fail('capabilities', 'knownGaps malformed');

  return c as unknown as SystemCapabilities;
}


export function validateCompanyPage(v: unknown): CompanyPage {
  const c = v as Record<string, unknown>;
  if (typeof c !== 'object' || c === null || !Array.isArray(c.items)) fail('companies', 'items array missing');
  for (const it of c.items as Record<string, unknown>[]) {
    if (!isStr(it.id) || !isStr(it.slug) || !isStr(it.name)) fail('companies', 'company row missing id/slug/name');
    if (typeof it.sourceScopes !== 'object' || typeof it.inventory !== 'object' || typeof it.tools !== 'object')
      fail('companies', `company ${String(it.slug)} missing sourceScopes/inventory/tools`);
  }
  if (c.nextCursor !== undefined && !isStr(c.nextCursor)) fail('companies', 'nextCursor present but not a string');
  return c as unknown as CompanyPage;
}

export function validateToolInventory(v: unknown): CompanyToolInventory {
  const t = v as Record<string, unknown>;
  if (typeof t !== 'object' || t === null) fail('company tools', 'body is not an object');
  if (!isStr(t.companyId) || !isStr(t.discoveryState)) fail('company tools', 'companyId/discoveryState missing');
  if (typeof t.unregisteredDetectionCount !== 'number') fail('company tools', 'unregisteredDetectionCount missing');
  if (!Array.isArray(t.items)) fail('company tools', 'items array missing');
  for (const it of t.items as Record<string, unknown>[]) {
    if (!isStr(it.id) || !isStr(it.toolId) || !isStr(it.status)) fail('company tools', 'tool row missing id/toolId/status');
    const d = it.display as Record<string, unknown>;
    if (typeof d !== 'object' || d === null || !isStr(d.label)) fail('company tools', `tool ${String(it.toolId)} missing display.label`);
  }
  return t as unknown as CompanyToolInventory;
}


const SYNC_STATES = ['REGISTERED', 'LISTING', 'DIFFING', 'PROBING', 'COMPLETE', 'FAILED', 'ACCESS_BLOCKED'] as const;

export function validateSyncHistory(v: unknown): SyncHistoryPage {
  const h = v as Record<string, unknown>;
  if (typeof h !== 'object' || h === null || !Array.isArray(h.items)) fail('sync history', 'items array missing');
  for (const it of h.items as Record<string, unknown>[]) {
    if (!isStr(it.syncRunId) || !isStr(it.bucketId)) fail('sync history', 'run row missing identity');
    if (!SYNC_STATES.includes(it.state as never)) fail('sync history', `state ${String(it.state)} outside the wire enum`);
    for (const n of ['listed', 'new', 'changed', 'deleted', 'unchanged'] as const) {
      if (typeof it[n] !== 'number') fail('sync history', `${n} count missing`);
    }
  }
  return h as unknown as SyncHistoryPage;
}
